import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { BulkOrderDateContext } from "../redistribution/models/BulkOrderDateContext.ts";
import type { PurchaseOrder } from "../redistribution/models/PurchaseOrder.ts";
import { clonePurchaseOrders } from "../utils/objectCloning.ts";

export interface QueuedRequest {
  requestId: string;
  submittedAt: string;
  completedAt?: number;
  status: "running" | "completed" | "failed" | "cancelled";
  errorMessage?: string;
  originalPurchaseOrders?: PurchaseOrder[];
  requestBody?: string;
  // Only set for Intermediate Order runs; the redistribution request drops the
  // Bulk Order dates, so they ride along here to reach the Results screen.
  bulkOrderDates?: BulkOrderDateContext[];
}

export const useRedistributionQueueStore = defineStore(
  "redistributionQueue",
  () => {
    const items = ref<QueuedRequest[]>([]);

    const runningCount = computed(
      () => items.value.filter((i) => i.status === "running").length
    );

    const canSubmit = computed(() => runningCount.value < 5);

    function enqueue(
      requestId: string,
      submittedAt: string,
      originalPurchaseOrders?: PurchaseOrder[],
      requestBody?: string,
      bulkOrderDates?: BulkOrderDateContext[]
    ): void {
      items.value.unshift({
        requestId,
        submittedAt,
        status: "running",
        ...(originalPurchaseOrders
          ? {
              originalPurchaseOrders: clonePurchaseOrders(
                originalPurchaseOrders
              ),
            }
          : {}),
        ...(requestBody === undefined ? {} : { requestBody }),
        ...(bulkOrderDates && bulkOrderDates.length > 0
          ? { bulkOrderDates }
          : {}),
      });
    }

    function markCompleted(requestId: string): void {
      const item = items.value.find((i) => i.requestId === requestId);
      if (item) {
        item.status = "completed";
        item.completedAt = Date.now();
      }
    }

    function markFailed(requestId: string, errorMessage?: string): void {
      const item = items.value.find((i) => i.requestId === requestId);
      if (item) {
        item.status = "failed";
        item.completedAt = Date.now();
        if (errorMessage !== undefined) {
          item.errorMessage = errorMessage;
        }
      }
    }

    function markCancelled(requestId: string): void {
      const item = items.value.find((i) => i.requestId === requestId);
      if (item) {
        item.status = "cancelled";
        item.completedAt = Date.now();
      }
    }

    function removeItem(requestId: string): void {
      items.value = items.value.filter((i) => i.requestId !== requestId);
    }

    function clear(): void {
      items.value = [];
    }

    return {
      items,
      runningCount,
      canSubmit,
      enqueue,
      markCompleted,
      markFailed,
      markCancelled,
      removeItem,
      clear,
    };
  }
);
